import {
  AddIcon,
  Button,
  DeleteIcon,
  DotsHorizontalIcon,
  Dropdown,
  DropdownMenu,
  EditIcon,
  SearchInput,
  Table,
  type TableColumns,
} from "@bp/ui-components";
import { useMemo, useState } from "react";
import { useMutation, useQuery } from "urql";
import { type DocumentType, graphql } from "../../client/gql";
import { FederatedFormModal } from "../../components/FederatedClients/FederatedFormModal/FederatedFormModal";
import { useConfirm } from "../../hooks/useConfirm";
import { Page } from "../../components/Page/Page";
import { PageToolbar } from "../../components/PageToolbar/PageToolbar";
import { showSuccessToast, showUserErrorToast } from "../../utils/toast";
import styles from "./FederatedClientsPage.module.scss";

const FederatedClientsQuery = graphql(`
  query pimFederatedClients($where: FederatedClientWhere) {
    federatedClients(where: $where) {
      uuid
      label
      name
      issuer
      clientId
      clientSecret
      responseTypes
      customIdClaims
    }
  }
`);

const DeleteFederatedClientsMutation = graphql(`
  mutation pimDeleteFederatedClients($clientId: String!) {
    deleteFederatedClients(where: { clientId: $clientId }) {
      nodesDeleted
    }
  }
`);

type FedRow = DocumentType<typeof FederatedClientsQuery>["federatedClients"][number];

export function FederatedClientsPage() {
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const [editTarget, setEditTarget] = useState<FedRow | null>(null);
  const [{ data }] = useQuery({ query: FederatedClientsQuery, variables: {} });
  const [, deleteFed] = useMutation(DeleteFederatedClientsMutation);
  const { confirm, ConfirmationDialog } = useConfirm();

  const feds = data?.federatedClients ?? [];
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return feds;
    return feds.filter(
      (f) =>
        (f.name ?? "").toLowerCase().includes(s) ||
        (f.issuer ?? "").toLowerCase().includes(s) ||
        (f.clientId ?? "").toLowerCase().includes(s),
    );
  }, [feds, q]);

  const onDelete = async (fed: FedRow) => {
    const ok = await confirm({
      title: "Föderierte Anbindung entfernen",
      message: `${fed.name || fed.label} wird entfernt. Anmeldungen über diese SSO-Anbindung schlagen danach fehl. Bereits angelegte Konten bleiben bestehen, verlieren aber die Verknüpfung.`,
      confirmText: "Löschen",
      cancelText: "Abbrechen",
    });
    if (!ok) {
      return;
    }
    const res = await deleteFed({ clientId: fed.clientId ?? "" });
    if (res.error) {
      showUserErrorToast({ text: "Löschen fehlgeschlagen", error: res.error });
      return;
    }
    showSuccessToast("Föderation entfernt");
  };

  const columns: TableColumns<FedRow>[] = [
    {
      id: "name",
      header: "Verbindung",
      accessorFn: (f) => f.name || f.label || "—",
      size: 280,
      cell: ({ row }) => (
        <div className={styles.cellMain}>
          <div style={{ minWidth: 0 }}>
            <div className={styles.name}>{row.original.name || row.original.label || "—"}</div>
            <div className={styles.sub}>{row.original.label}</div>
          </div>
        </div>
      ),
    },
    {
      id: "protocol",
      header: "Protokoll",
      size: 160,
      cell: () => <span className={styles.textSm}>OpenID Connect</span>,
    },
    {
      id: "issuer",
      header: "Issuer / ClientId",
      accessorFn: (f) => f.issuer ?? "",
      size: 320,
      cell: ({ row }) => (
        <div style={{ minWidth: 0 }}>
          <code className={styles.codepill}>{row.original.clientId || "—"}</code>
          <div className={styles.subIssuer}>{row.original.issuer}</div>
        </div>
      ),
    },
    {
      id: "actions",
      header: "",
      size: 56,
      cell: ({ row }) => (
        <Dropdown
          trigger={
            <Button
              hierarchy="tertiary"
              icon={<DotsHorizontalIcon />}
              ariaLabel="Aktionen"
              onClick={(e) => e.stopPropagation()}
            />
          }
        >
          <DropdownMenu
            data={[
              {
                label: "Bearbeiten",
                onClick: () => setEditTarget(row.original),
              },
              {
                label: "Löschen",
                color: "error",
                onClick: () => void onDelete(row.original),
              },
            ]}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <Page
      title="Föderation"
      description="SSO-Anbindungen der Schulen — externe Identity-Provider, mit denen der Auth-Server föderiert."
      actions={[
        {
          text: "Anbindung hinzufügen",
          icon: <AddIcon />,
          hierarchy: "primary",
          callback: () => setAdding(true),
        },
      ]}
    >
      <PageToolbar>
        <SearchInput
          name="fed-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Name, Issuer oder Client-ID …"
        />
      </PageToolbar>
      <Table<FedRow>
        columns={columns}
        data={filtered}
        isDesktop
        showSort
        showRowHover
        showShadow
        showBorderRadius
        onRowClick={(_, row) => setEditTarget(row.original)}
      />
      {adding && (
        <FederatedFormModal
          mode="create"
          onClose={() => setAdding(false)}
          onSaved={() => setAdding(false)}
        />
      )}
      {editTarget && (
        <FederatedFormModal
          mode="edit"
          fed={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => setEditTarget(null)}
        />
      )}
      <ConfirmationDialog />
    </Page>
  );
}
